import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { IconRotateClockwise2 } from '@tabler/icons-react'
import * as React from "react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "ring-offset-background focus-visible:ring-ring inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors hover:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive",
        outline:
          "border-input bg-background hover:bg-accent hover:text-accent-foreground border",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "size-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
	onHover?: (hovering: boolean) => void
  asChild?: boolean
	isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, onHover, onMouseEnter, onMouseLeave, asChild = false, isLoading = false,
			children, ...props }, ref) => {
		const handleMouseEnter = (event: React.MouseEvent<HTMLButtonElement>) => {
			onHover?.(true)
			onMouseEnter?.(event)
		}

		const handleMouseLeave = (event: React.MouseEvent<HTMLButtonElement>) => {
			onHover?.(false)
			onMouseLeave?.(event)
		}
    const Comp = asChild ? Slot : "button"

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
				disabled={isLoading || props.disabled} // Disable button when loading
				onMouseEnter={handleMouseEnter}
      	onMouseLeave={handleMouseLeave}
        {...props}
      >
				{isLoading ? (
					<IconRotateClockwise2 className="animate-spin" />
				) : (
					children
				)}
 			</Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
